import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function TexasToIreland() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openRelocationExpert = () => {
    const expertButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (expertButton) {
      expertButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      <article className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="relative min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/08ee722a4797058c6c6fb1115313d0df.jpg"
              alt="Texas to Dublin Ireland relocation comparison"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <span className="text-2xl">🤠</span>
              <i className="ri-arrow-right-line text-white text-xl"></i>
              <span className="text-2xl">☘️</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Moving to Ireland from Texas: Complete Guide for Texans
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Everything Texas residents need to know about relocating to Ireland in 2024
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Making the move from Texas to Ireland is a significant lifestyle change, but thousands of Texans have successfully made this transition. Whether you're drawn by career opportunities in Dublin's booming tech sector, seeking a different pace of life, or exploring your Irish heritage, this comprehensive guide covers everything Texans need to know about relocating to Ireland.
            </p>
          </div>

          {/* Biggest Differences */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Biggest Differences: Texas vs Ireland</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: 'ri-temp-cold-line',
                  title: 'Weather',
                  texas: '100°F summers, mild winters',
                  ireland: '60-70°F summers, 40-50°F winters',
                  impact: 'Texans will need to adjust to cooler, rainier weather year-round. No more scorching summers!',
                  color: 'blue',
                },
                {
                  icon: 'ri-car-line',
                  title: 'Transportation',
                  texas: 'Car essential, wide highways',
                  ireland: 'Public transit, narrow roads, left-side driving',
                  impact: 'Most Texans can live without a car in Dublin. If driving, prepare for smaller roads and roundabouts.',
                  color: 'emerald',
                },
                {
                  icon: 'ri-home-4-line',
                  title: 'Housing Size',
                  texas: '2,000+ sq ft homes common',
                  ireland: '800-1,200 sq ft apartments typical',
                  impact: 'Texans will downsize significantly. Irish homes are much smaller but more affordable than Texas cities.',
                  color: 'amber',
                },
                {
                  icon: 'ri-time-line',
                  title: 'Work-Life Balance',
                  texas: '40-50 hour weeks, 10-15 vacation days',
                  ireland: '35-40 hour weeks, 20-25 vacation days',
                  impact: 'Texans gain significantly more time off and better work-life balance in Ireland.',
                  color: 'purple',
                },
              ].map((diff, index) => (
                <div key={index} className={`bg-${diff.color}-50 border-2 border-${diff.color}-200 rounded-xl p-6`}>
                  <div className={`w-12 h-12 bg-${diff.color}-100 rounded-lg flex items-center justify-center mb-4`}>
                    <i className={`${diff.icon} text-2xl text-${diff.color}-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{diff.title}</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-semibold text-gray-700 mb-1">🤠 Texas:</p>
                      <p className="text-gray-600">{diff.texas}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700 mb-1">☘️ Ireland:</p>
                      <p className="text-gray-600">{diff.ireland}</p>
                    </div>
                    <div className={`bg-${diff.color}-100 rounded-lg p-3`}>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Impact for Texans:</p>
                      <p className="text-gray-700">{diff.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Cost Comparison */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cost of Living: Texas vs Ireland</h2>
            
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Category</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Austin, TX</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Dublin, Ireland</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-900">Difference</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">1-Bed Apartment (City Center)</td>
                      <td className="py-3 px-4">$1,800/month</td>
                      <td className="py-3 px-4">$2,100/month</td>
                      <td className="py-3 px-4 text-red-600 font-semibold">+$300/mo</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Groceries (Monthly)</td>
                      <td className="py-3 px-4">$350</td>
                      <td className="py-3 px-4">$400</td>
                      <td className="py-3 px-4 text-red-600 font-semibold">+$50/mo</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Gas/Petrol (per gallon)</td>
                      <td className="py-3 px-4">$3.20</td>
                      <td className="py-3 px-4">$7.50</td>
                      <td className="py-3 px-4 text-red-600 font-semibold">+$4.30</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">Healthcare (Annual)</td>
                      <td className="py-3 px-4">$6,000+</td>
                      <td className="py-3 px-4">$1,500</td>
                      <td className="py-3 px-4 text-green-600 font-semibold">Save $4,500/yr</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3 px-4 font-semibold">State Income Tax</td>
                      <td className="py-3 px-4">0% (No state tax!)</td>
                      <td className="py-3 px-4">20-40%</td>
                      <td className="py-3 px-4 text-red-600 font-semibold">Higher taxes</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-semibold">BBQ Brisket</td>
                      <td className="py-3 px-4">$18/lb (Franklin's)</td>
                      <td className="py-3 px-4">Hard to find!</td>
                      <td className="py-3 px-4 text-amber-600 font-semibold">You'll miss this</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-amber-900 mb-3">Bottom Line for Texans</h3>
              <p className="text-amber-800">
                While Texans will pay higher taxes and slightly more for housing, the <strong>massive healthcare savings</strong> ($4,500-7,000/year) and better work-life balance (20+ vacation days vs 10-15) make Ireland financially attractive. Plus, no more $300+ monthly AC bills in summer!
              </p>
            </div>
          </section>

          {/* Jobs for Texans */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Jobs in Ireland for Texans</h2>
            
            <p className="text-lg text-gray-700 mb-6">
              Texas has a strong tech sector (Austin), energy industry (Houston), and healthcare (Dallas). Many of these skills translate well to Ireland's job market. Several Texas-based companies also have Irish operations.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Texas Companies in Ireland</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Dell Technologies:</strong> Major operations in Limerick and Cork</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Indeed:</strong> European headquarters in Dublin</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Oracle:</strong> Large presence in Dublin</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span><strong>PayPal:</strong> International operations center in Dublin</span>
                  </li>
                </ul>
              </div>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">In-Demand Skills from Texas</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-code-box-line text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Software Development:</strong> €60k-120k ($65k-130k)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-database-2-line text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Data Analytics:</strong> €55k-100k ($60k-109k)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-customer-service-line text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Sales & Marketing:</strong> €45k-85k ($49k-93k)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-hospital-line text-emerald-600 flex-shrink-0 mt-1"></i>
                    <span><strong>Healthcare:</strong> €50k-90k ($55k-98k)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* What Texans Will Miss */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Texans Will Miss (and Love) About Ireland</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-emotion-sad-line text-red-600"></i>
                  You'll Miss
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span>Texas BBQ (brisket, ribs, authentic Tex-Mex)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span>Warm, sunny weather year-round</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span>Spacious homes and big yards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span>No state income tax</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span>H-E-B grocery stores</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span>College football Saturdays</span>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-emotion-happy-line text-green-600"></i>
                  You'll Love
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span>20-25 vacation days (vs 10-15 in Texas)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span>Affordable healthcare (no $500/month premiums)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span>Easy travel to Europe (€20 flights to Paris, Barcelona)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span>Walkable cities (no 30-minute drives everywhere)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span>Pub culture and live music every night</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-1"></i>
                    <span>No more 100°F summers!</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#0A381F] to-emerald-800 text-white rounded-2xl p-8">
              <h2 className="text-3xl font-bold mb-6">Ready to Trade Texas for Ireland?</h2>
              <p className="text-lg mb-6 text-white/90">
                Our Ireland Relocation Expert can help you navigate every step of your move from Texas to Ireland. Get personalized guidance on visas, housing, jobs, and adjusting to Irish life.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openRelocationExpert}
                  className="bg-white text-[#0A381F] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-chat-voice-fill text-xl"></i>
                  Talk to Relocation Expert
                </button>
                <a
                  href="/tools/cost-calculator"
                  className="bg-[#D4AF37] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#C19B2F] transition-all hover-lift flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-calculator-line text-xl"></i>
                  Calculate Your Costs
                </a>
              </div>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}